// Run log of a scenario or of one record: status, steps done and the reason of a stop or error (decision 008).

import { useEffect, useState } from 'react';
import { recordTitle, type RunLogEntry, type RunStatus } from '@confdeck/core-shared';
import { ago } from '../fields/format.ts';
import { useMeta, useStatus, useSync } from '../sync/react.tsx';
import { Chip } from '../ui/primitives.tsx';
import { navigate, recordHref } from '../shell/router.ts';
import s from './automations.module.css';

interface Run { id: string; automationId: string; recordId: string | null; status: RunStatus; attempts: number; log: RunLogEntry[]; error: string | null; createdAt: string; updatedAt: string; nextAt: string | null }

const STATUS: Record<RunStatus, [string, string]> = {
  pending: ['ждёт', 'gray'], running: ['идёт', 'blue'], waiting: ['пауза', 'amber'], succeeded: ['выполнен', 'green'], failed: ['ошибка', 'red'], skipped: ['пропущен', 'gray'],
};

export function RunLog({ automationId, recordId }: { automationId?: string; recordId?: string }) {
  const client = useSync();
  const meta = useMeta();
  useStatus();
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    // A refusal of the server is not an empty log: the last list stays, and the first load says it failed.
    const load = () => fetch(`/api/automation-runs?${automationId ? `automation=${automationId}` : `record=${recordId}`}`)
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json() as Promise<{ items: Run[] }>; })
      .then((j) => alive && setRuns(j.items)).catch(() => alive && setFailed(true));
    void load();
    // Runs live on the server only; a short poll keeps the open log fresh while scenarios work.
    const t = setInterval(load, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [automationId, recordId]);
  if (!runs) return <div className={s.hint}>{failed ? 'Журнал запусков не загрузился. Обновите страницу.' : '\u00a0'}</div>;
  if (!runs.length) return <div className={s.hint}>{automationId ? 'Запусков пока не было. Сценарий сработает на следующих изменениях.' : 'Автоматика с этой записью пока ничего не делала.'}</div>;
  return (
    <ol className={s.runs}>
      {runs.map((r) => {
        const rec = r.recordId ? client.store.record(r.recordId) : undefined;
        const a = meta.automation(r.automationId);
        const [label, color] = STATUS[r.status];
        return (
          <li key={r.id} className={s.run}>
            <div className={s.runHead}>
              <Chip color={color} dot>{label}</Chip>
              {automationId ? (
                <button type="button" className={s.runRec} onClick={() => { if (rec) navigate(recordHref(rec.id)); }}>{rec ? recordTitle(rec, meta) : 'запись недоступна'}</button>
              ) : <button type="button" className={s.runRec} onClick={() => navigate(`/a/${r.automationId}`)}>{a?.name ?? 'сценарий'}</button>}
              <span className={s.runTime}>{ago(r.createdAt)}{r.attempts > 1 ? ` · попыток ${r.attempts}` : ''}</span>
            </div>
            {(r.error || r.log.some((l) => l.note)) && (
              <div className={s.runLog}>
                {r.log.filter((l) => l.note).map((l, i) => <div key={i}>{l.note}</div>)}
                {r.error && <div className={s.runErr}>{r.error}</div>}
                {r.status === 'waiting' && r.nextAt && <div>Продолжит {ago(r.nextAt)}</div>}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
